/**
 * Analytical 2-bone IK solver for limb chains (arms and legs).
 *
 * Uses trigonometric (law of cosines) solution to compute the exact joint
 * positions for a 2-bone chain reaching toward a target. Unlike iterative solvers,
 * this produces a deterministic result in a single step with no iterations,
 * no convergence issues, and no need for displacement clamping.
 *
 * The pole direction defines the bend plane (e.g., knees bend backward,
 * elbows bend forward).
 *
 * An optional view-plane depth bias keeps the mid-joint near its original
 * screen-space depth, preventing limbs from freely swinging into/out of
 * the viewport when the user drags in screen space.
 */

import * as THREE from "three/webgpu";
import { logAnalyticalFullExtend, logAnalyticalSolve } from "./ikDebugLog.ts";
import { directionsToLocalQuaternions, type Quat4 } from "./quaternionMath.ts";

// Scratch objects — reused across calls to avoid per-frame allocations
const _rootToTarget = new THREE.Vector3();
const _polePerp = new THREE.Vector3();
const _bendAxis = new THREE.Vector3();
const _midPos = new THREE.Vector3();
const _origDir = new THREE.Vector3();

/** Maximum world-space depth correction for the mid-joint (elbow/knee). */
const MAX_DEPTH_CORRECTION = 0.08;

/**
 * Solve a 2-bone IK chain analytically.
 *
 * @param rootPos         World position of the chain root (e.g., shoulder, hip)
 * @param targetPos       World position the end effector should reach
 * @param upperLength     Length of the upper bone (shoulder→elbow, hip→knee)
 * @param lowerLength     Length of the lower bone (elbow→wrist, knee→ankle)
 * @param poleDirection   World-space direction defining the bend plane
 * @param origPositions   Original world positions [root, mid, tip] for quaternion conversion
 * @param startLocalQuats Local quaternions at solve start [upper, lower]
 * @param parentWorldQuats Parent world quaternions at solve start [upper's parent, lower's parent]
 * @param viewBias        Optional: { forward, origMidDepth, strength } to keep mid-joint
 *                        near its original screen-space depth.
 * @returns Map of boneName → quaternion [x,y,z,w] for the two bones
 */
export function solveAnalytical2Bone(
  rootPos: THREE.Vector3,
  targetPos: THREE.Vector3,
  upperLength: number,
  lowerLength: number,
  poleDirection: THREE.Vector3,
  origPositions: THREE.Vector3[],
  boneNames: string[],
  startLocalQuats: THREE.Quaternion[],
  parentWorldQuats: THREE.Quaternion[],
  viewBias?: { forward: THREE.Vector3; origMidDepth: number; strength: number },
): Map<string, Quat4> {
  // Distance from root to target
  _rootToTarget.subVectors(targetPos, rootPos);
  let dist = _rootToTarget.length();

  // Clamp to reachable range
  const maxReach = upperLength + lowerLength;
  const minReach = Math.abs(upperLength - lowerLength);
  const EPS = 1e-6;

  if (dist < EPS) {
    // Target is at root — fully folded, use pole direction to orient
    dist = EPS;
    _rootToTarget.copy(poleDirection).normalize().multiplyScalar(EPS);
  }

  if (dist >= maxReach) {
    // Fully extended toward target
    logAnalyticalFullExtend(boneNames[0], dist, maxReach);
    const dir = _rootToTarget.normalize();
    const midWorld = _midPos.copy(rootPos).addScaledVector(dir, upperLength);
    return directionsToLocalQuaternions(
      origPositions,
      [rootPos, midWorld, targetPos],
      boneNames,
      startLocalQuats,
      parentWorldQuats,
      parentWorldQuats[0],
    );
  }

  if (dist <= minReach + EPS) {
    dist = minReach + EPS;
  }

  // Law of cosines: angle at root
  // cos(rootAngle) = (L1² + d² - L2²) / (2·L1·d)
  const cosRootAngle =
    (upperLength * upperLength + dist * dist - lowerLength * lowerLength) /
    (2 * upperLength * dist);
  const rootAngle = Math.acos(Math.max(-1, Math.min(1, cosRootAngle)));

  // Direction from root to target (normalized)
  const chainDir = _rootToTarget.normalize();

  // Construct the bend plane using the pole direction.
  // Remove the component of poleDirection along chainDir to get the perpendicular.
  const poleDot = chainDir.dot(poleDirection);
  _polePerp.copy(poleDirection).addScaledVector(chainDir, -poleDot);
  const perpLen = _polePerp.length();

  if (perpLen < EPS) {
    // Pole direction is parallel to chain — pick an arbitrary perpendicular
    _polePerp.set(0, 0, 1);
    const d2 = chainDir.dot(_polePerp);
    _polePerp.addScaledVector(chainDir, -d2);
    if (_polePerp.length() < EPS) {
      _polePerp.set(0, 1, 0);
      const d3 = chainDir.dot(_polePerp);
      _polePerp.addScaledVector(chainDir, -d3);
    }
  }
  _polePerp.normalize();

  // Bend axis is perpendicular to both chainDir and polePerp
  _bendAxis.crossVectors(chainDir, _polePerp).normalize();

  // Place the mid-joint: rotate chainDir by rootAngle toward polePerp
  // midDir = chainDir * cos(rootAngle) + polePerp * sin(rootAngle)
  const sinRoot = Math.sin(rootAngle);
  const cosRoot = Math.cos(rootAngle);
  const midWorld = _midPos
    .copy(rootPos)
    .addScaledVector(chainDir, upperLength * cosRoot)
    .addScaledVector(_polePerp, upperLength * sinRoot);

  // Apply view-plane depth bias: pull mid-joint toward its original
  // screen-space depth to keep motion in the screen plane.
  let depthDeltaForLog = 0;
  if (viewBias && viewBias.strength > 0) {
    const currentDepth = midWorld.dot(viewBias.forward);
    // Clamp depth correction to prevent large corrections that push
    // the joint rotation outside constraint bounds (especially asymmetric_ball).
    const rawDelta = currentDepth - viewBias.origMidDepth;
    const depthDelta = Math.max(
      -MAX_DEPTH_CORRECTION,
      Math.min(MAX_DEPTH_CORRECTION, rawDelta),
    );
    depthDeltaForLog = rawDelta;
    midWorld.addScaledVector(viewBias.forward, -depthDelta * viewBias.strength);
    // Re-enforce upper bone length from root
    _origDir.subVectors(midWorld, rootPos);
    const midDist = _origDir.length();
    if (midDist > EPS) {
      _origDir.multiplyScalar(upperLength / midDist);
      midWorld.copy(rootPos).add(_origDir);
    }
  }

  logAnalyticalSolve(boneNames[0], dist, maxReach, rootAngle, depthDeltaForLog);

  return directionsToLocalQuaternions(
    origPositions,
    [rootPos, midWorld, targetPos],
    boneNames,
    startLocalQuats,
    parentWorldQuats,
    parentWorldQuats[0],
  );
}
